// Copyright (c) 2026. Hardfin, Inc. All rights reserved.

/**
 * Shared sparse entity types for the Hardfin application.
 * This module contains common sparse schemas to avoid circular imports between domains.
 * These schemas represent the minimum required fields to identify and display entities.
 */
import { caseInsensitiveLowerEnum } from '@/constants/case-insensitive-enum';
import { projectStatusSchema } from '@/constants/project-status';
import { unitOfMeasureSchema } from '@/constants/unit-of-measure';
import { itemTypeSchema } from '@/items/constants/item-types';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { integrationListSchema } from '@/utilities/integration';
import { z } from 'zod';

/**
 * Schema for a sparse item in the Item Catalog.
 * Used for BOM items and other sparse item references.
 */
export const sparseItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  type: itemTypeSchema,
  sku: z.string(),
  isArchived: z.boolean(),
  unitOfMeasure: unitOfMeasureSchema,
  hasAssets: z.boolean(),
  isPhysical: z.boolean(),
  shouldRenderSerialFields: z.boolean(),
});

export type SparseItem = z.infer<typeof sparseItemSchema>;

/**
 * Basic item field schema.
 * Contains the minimum required fields to identify and display an item field.
 * Uses the same field types as productFieldTypeSchema to ensure compatibility.
 */
export const sparseItemFieldSchema = z.object({
  id: z.string(),
  key: z.string(),
  type: caseInsensitiveLowerEnum([
    'text',
    'multiline_text',
    'integer',
    'number',
    'boolean',
    'date',
    'time',
    'date_time',
  ]),
  label: z.string(),
  order: z.number(),
  section: z.number(),
  metadataCount: z.number().optional(),
});

/**
 * Valid location types in the system.
 */
const locationTypeSchema = z.enum(['SITE', 'ZONE']);

/**
 * Basic customer information schema.
 * Contains the minimum required fields to identify and display a customer.
 */
export const sparseCustomerSchema = z.object({
  key: z.string(),
  id: z.string(),
  name: z.string(),
  isArchived: z.boolean(),
  isCustomer: z.boolean(),
  isSupplier: z.boolean(),
  // Virtualized field for frontend use only — not sent by the backend.
  // Enables a standardized schema for distinguishing manufacturer vs customer
  // options in company search/autocomplete components.
  isManufacturer: z.boolean().default(false),
});

export type SparseCustomer = z.infer<typeof sparseCustomerSchema>;

/**
 * Basic project identifier schema.
 * Contains the minimum required fields to identify a project.
 */
export const projectIdentifierSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  isArchived: z.boolean().catch(false),
});

/**
 * Basic project information schema.
 * Contains the minimum required fields to identify and display a project,
 * plus the billing defaults (paymentTermLabel + purchaseOrderNumber) so
 * invoice-create forms can seed defaults without a paired lookup. The
 * full Project schema overrides paymentTerm with the resolved object, so
 * the sparse field is named `paymentTermLabel` to keep the two distinct.
 */
export const sparseProjectSchema = projectIdentifierSchema.extend({
  status: projectStatusSchema.catch('DATE_RANGE_INCOMPLETE'),
  startOn: dateSchema.nullable(),
  endOn: dateSchema.nullable(),
  customer: sparseCustomerSchema.nullable(),
  integrations: integrationListSchema,
  paymentTermLabel: z.string().nullable(),
  purchaseOrderNumber: z.string().nullable(),
});

export type SparseProject = z.infer<typeof sparseProjectSchema>;

/**
 * Basic person information schema.
 * Contains the minimum required fields to identify and display a person.
 * Matches the backend sparse.Person type (no email field).
 */
export const sparsePersonSchema = z.object({
  id: z.string(),
  key: z.string(),
  firstName: z.string().catch(''),
  lastName: z.string().catch(''),
});

export type SparsePerson = z.infer<typeof sparsePersonSchema>;

/**
 * Schema for detailed address components of a location.
 * Uses international-friendly field names.
 */
export const locationComponentsSchema = z.object({
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  locality: z.string().nullable(),
  administrativeArea: z.string().nullable(),
  postalCode: z.string().nullable(),
  countryCode: z.string().nullable(),
  formattedAddress: z.string().nullable(),
});

export type LocationComponents = z.infer<typeof locationComponentsSchema>;

/**
 * Schema for parsing address components from backend API responses.
 * Transforms backend field names (city, state, country) to frontend
 * field names (locality, administrativeArea, countryCode).
 * Also accepts frontend field names directly for test compatibility.
 */
const locationComponentsFromApiSchema = z
  .object({
    addressLine1: z.string().nullable(),
    addressLine2: z.string().nullable(),
    city: z.string().nullable().optional(),
    state: z.string().nullable().optional(),
    postalCode: z.string().nullable(),
    country: z.string().nullable().optional(),
    locality: z.string().nullable().optional(),
    administrativeArea: z.string().nullable().optional(),
    countryCode: z.string().nullable().optional(),
    formattedAddress: z.string().nullable(),
  })
  .transform(
    ({
      city,
      state,
      country,
      locality,
      administrativeArea,
      countryCode,
      ...rest
    }) => ({
      ...rest,
      locality: locality ?? city ?? null,
      administrativeArea: administrativeArea ?? state ?? null,
      countryCode: countryCode ?? country ?? null,
    })
  );

/**
 * Basic zone information schema.
 * Contains the minimum required fields to identify and display a zone.
 */
const sparseZoneSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  consignee: sparseCustomerSchema.nullable(),
  assetCount: z.number(),
  isArchived: z.boolean(),
  isInventory: z.boolean(),
});

export type SparseZone = z.infer<typeof sparseZoneSchema>;

/**
 * Basic location information schema.
 * Contains the minimum required fields to identify and display a location.
 */
export const sparseLocationSchema = z.object({
  type: locationTypeSchema,
  key: z.string(),
  address: locationComponentsFromApiSchema.nullable(),
  id: z.string(),
  name: z.string(),
  customer: sparseCustomerSchema.nullable(),
  isArchived: z.boolean(),
  latitude: z.string().nullable(),
  longitude: z.string().nullable(),
  googlePlaceId: z.string().nullable(),
  isInventory: z.boolean(),
  isTransient: z.boolean(),
  isThirdPartyEnabled: z.boolean(),
  thirdPartyIdentifier: z.string().nullable(),
  description: z.string().nullable(),
  isInventoryOverride: z.boolean(),
  consignee: sparseCustomerSchema.nullable(),
  zoneCount: z.number(),
  archivedZoneCount: z.number(),
  zones: z
    .array(sparseZoneSchema)
    .nullable()
    .transform((v) => v ?? []),
});

export type SparseLocation = z.infer<typeof sparseLocationSchema>;

export const sparseLocationWithParentSchema = sparseLocationSchema.extend({
  parentLocation: z.lazy(() => sparseLocationSchema).nullable(),
});

export type SparseLocationWithParent = z.infer<
  typeof sparseLocationWithParentSchema
>;

/**
 * Sparse location with the integration link statuses for the location.
 * Used on order and shipment responses so warehouse-facing pages can surface
 * an SFDC drift indicator next to each linked origin/destination location.
 */
export const sparseLocationWithIntegrationSchema =
  sparseLocationWithParentSchema.extend({
    integrations: integrationListSchema,
  });

/**
 * Basic shipment information schema.
 * Contains the minimum required fields to identify and display a shipment.
 */
export const sparseShipmentSchema = z.object({
  id: z.string(),
  key: z.string(),
  shippedAt: z.coerce.date().nullable(),
  deliveredAt: z.coerce.date().nullable(),
  shipmentNumber: z.string(),
  shipmentType: z.enum(['INBOUND', 'OUTBOUND']),
});

export type SparseShipment = z.infer<typeof sparseShipmentSchema>;

/**
 * Fulfillment order item with quantity schema.
 * Contains the minimum required fields to identify an item in a fulfillment order.
 * Uses nested item structure where line item binding identifiers (id, key) are at the
 * top level, and catalog item properties are nested under `item`.
 */
const sparseItemWithQuantitySchema = z.object({
  // Order line item identifiers (binding id/key)
  id: z.string(),
  key: z.string(),
  // Nested catalog item properties
  item: sparseItemSchema,
  // Quantity fields
  requestedQuantity: z.number().nullish(),
  pendingQuantity: z.number().nullish(),
  receivedQuantity: z.number().nullish(),
  shippedQuantity: z.number().nullish(),
  deliveredQuantity: z.number().nullish(),
});

/**
 * Basic fulfillment order information schema.
 * Contains the minimum required fields to identify and display a fulfillment order.
 */
export const sparseFulfillmentSchema = z.object({
  id: z.string(),
  key: z.string(),
  fulfillmentOrderNumber: z.string(),
  // Whether a third-party logistics provider fulfills this order
  isThirdPartyFulfilled: z.boolean(),
  to: sparseLocationWithParentSchema.nullable(),
  from: sparseLocationWithParentSchema.nullable(),
  items: z.array(sparseItemWithQuantitySchema),
  project: sparseProjectSchema.nullable().optional(),
  promiseShipDate: dateSchema.nullable().optional(),
  promiseDeliveryDate: dateSchema.nullable().optional(),
  // Set once the order is closed, which stops it taking further shipments
  closedAt: dateTimeSchema.nullable().optional(),
});

export type SparseFulfillment = z.infer<typeof sparseFulfillmentSchema>;

/**
 * Basic purchase order information schema.
 * Contains the minimum required fields to identify and display a purchase order.
 */
export const sparsePOSchema = z.object({
  id: z.string(),
  key: z.string(),
  purchaseOrderNumber: z.string(),
  externalPurchaseOrderNumber: z.string().nullable(),
  to: sparseLocationWithParentSchema.nullable(),
  from: sparseLocationWithParentSchema.nullable(),
  supplier: sparseCustomerSchema.nullable().optional(),
  items: z.array(sparseItemWithQuantitySchema),
  promiseShipDate: dateSchema.nullable().optional(),
  promiseDeliveryDate: dateSchema.nullable().optional(),
});

type SparsePOSchema = z.infer<typeof sparsePOSchema>;

/**
 * Alias for SparsePOSchema to match usage in shipment dialogs.
 */
export type SparsePurchaseOrder = SparsePOSchema;

/**
 * Basic return order information schema.
 * Contains the minimum required fields to identify and display a return order.
 */
export const sparseROSchema = z.object({
  id: z.string(),
  key: z.string(),
  returnOrderNumber: z.string(),
  to: sparseLocationWithParentSchema.nullable(),
  from: sparseLocationWithParentSchema.nullable(),
  items: z.array(sparseItemWithQuantitySchema),
  promiseShipDate: dateSchema.nullable().optional(),
  promiseDeliveryDate: dateSchema.nullable().optional(),
});

/**
 * Minimal fulfillment order reference schema.
 * Used within sparse exchange order to show linked FOs.
 */
const exchangeOrderFulfillmentSchema = z.object({
  id: z.string(),
  key: z.string(),
  fulfillmentOrderNumber: z.string(),
});

/**
 * Minimal return order reference schema.
 * Used within sparse exchange order to show linked ROs.
 */
const exchangeOrderReturnOrderSchema = z.object({
  id: z.string(),
  key: z.string(),
  returnOrderNumber: z.string(),
});

/**
 * Basic exchange order information schema.
 * Contains the minimum required fields to display exchange order context on FO/RO.
 */
export const sparseExchangeOrderSchema = z.object({
  id: z.string(),
  key: z.string(),
  exchangeNumber: z.string(),
  status: z.string(),
  fulfillmentOrders: z.array(exchangeOrderFulfillmentSchema),
  returnOrders: z.array(exchangeOrderReturnOrderSchema),
});

/**
 * Basic batch information schema.
 * Contains the minimum required fields for displaying batch details in lot views.
 */
export const sparseBatchSchema = z.object({
  id: z.string(),
  receivedAt: z.coerce.date(),
  receivedQuantity: z.number(),
  receiveType: z.string(),
  shipmentReference: z.string().nullable(),
});
